import { Injectable, NgZone } from '@angular/core';
import { take } from 'rxjs/operators';
import _ from 'lodash';

import { SUBREGION_KIND, Suburb } from '../../../../models/content';
import { ModalService } from '../../../../services/modal.service';
import { PicalunaSiteService } from '../../../../services/picaluna-site.service';
import { SuburbAreasService } from '../../../../services/suburb-areas.service';
import { suburbPublishScope } from '../content/suburb-page';
import { SuburbPublishAction } from '../page/content-suburb.component';
import { SuburbCatalogueService } from './catalogue.service';
import { ContentRecordsService } from './content-records.service';
import { publishNotSetUp } from './api-errors';
import { inZone } from './in-zone';
import { MapEditsService } from './map-edits.service';
import { SubRegionsService } from './subregions.service';
import { SuburbDetailService } from './suburb-detail.service';
import { WorkspaceStateService } from './workspace-state.service';

type PublishAction = 'publish' | 'unpublish';

/**
 * A suburb's or sub-region's website page from the Suburbs tool (#253, #257, #260, #262): Publish and Un-publish
 * without opening the editor (the record's published flag, written on a freshly read copy; then shipped to the
 * website preview on its own), what follows any page save, and Remove for a page that isn't on the website. The page
 * is the template of its kind filled from the record, so no CMS page is written.
 */
@Injectable()
export class PagePublisherService {
  constructor(
    private records: ContentRecordsService,
    private site: PicalunaSiteService,
    private modalService: ModalService,
    private api: SuburbAreasService,
    private zone: NgZone,
    private state: WorkspaceStateService,
    private catalogue: SuburbCatalogueService,
    private edits: MapEditsService,
    private subRegions: SubRegionsService,
    private detail: SuburbDetailService
  ) {}

  /** The selected suburb's page record, as cached (null: no page yet). */
  get selectedRecord(): Suburb | undefined {
    return this.records.suburbRecord(this.state.selectedCode);
  }

  /** Whether the selected suburb's page is on the website. */
  get selectedLive(): boolean {
    return this.selectedRecord?.published === true;
  }

  /** Publish or Un-publish the selected suburb's page from its Overview. */
  publishSelected(action: PublishAction): void {
    const code = this.state.selectedCode;
    const regionId = this.state.selectedArea.regionId;
    if (!code || !regionId || this.state.readOnly) return;
    this.publish(action, regionId, () => {
      const record = this.selectedRecord;
      return record
        ? _.cloneDeep(record)
        : this.records.newSuburb(
            code,
            regionId,
            this.detail.name || this.state.engine.areaName(code) || ''
          );
    });
  }

  /** Publish or Un-publish the open sub-region's page (its record and its page, on their own). */
  publishSubRegion(action: PublishAction): void {
    const record = this.subRegions.record;
    if (!record || this.state.readOnly) return;
    this.publish(action, record.regionId, () => _.cloneDeep(this.subRegions.record ?? record));
  }

  /** After a page save (here or in the Page tab): the cached copy, the map's page dots, and shipping it if published. */
  afterSave(record: Suburb, publishAction: SuburbPublishAction): void {
    this.records.cache('suburbs', record);
    void this.catalogue.reload();
    // "Covered" now just means "has a page" (the server keeps such a suburb in its region); Publish sets it.
    if (publishAction === 'publish' && record.salCode) this.markHasPage(record.salCode);
    if (publishAction && record.kind === SUBREGION_KIND) this.edits.clearStale(record._meta.id);
    this.subRegions.reloadIfOpen();
    if (!publishAction) {
      this.records.markRegionChanged(record.regionId);
      this.state.setStatus(
        `Saved the page for ${record.name}.${record.published ? ' Publish it again to put the changes on the website.' : ''}`
      );
      this.state.redraw();
      return;
    }
    this.ship(record, publishAction);
  }

  /** A draft was accepted (#254): the server has the record; keep the cached copy in step so the Page tab shows it. */
  draftAccepted(r: { intro?: string; body?: string }): void {
    const record = this.selectedRecord;
    if (!record) return;
    this.records.cache('suburbs', {
      ...record,
      ...(r.intro !== undefined ? { intro: r.intro } : {}),
      ...(r.body !== undefined ? { body: r.body } : {}),
    });
    this.state.redraw();
  }

  /** Delete the selected suburb's page that isn't on the website: its record and its designed page. */
  removeSelected(): void {
    const record = this.selectedRecord;
    if (!record || record.published === true) return;
    const copy = `Delete the page for "${record.name}"? It isn't on the website, so nothing there changes. Publishing the suburb again builds it from the Suburb template.`;
    this.records.removeWithPage(
      record,
      this.records.designedPage(record),
      { title: 'Remove page', copy, label: 'Remove' },
      () => {
        this.state.setStatus(`Removed the page for ${record.name}.`);
        void this.catalogue.reload();
      }
    );
  }

  /** Save the record as published or not (a new one as it is, else on a freshly read copy), then ship it. */
  private publish(action: PublishAction, regionId: string, recordFor: () => Suburb): void {
    const regions = this.records.regions();
    const region = regions.find((r) => r._meta.id === regionId);
    if (!region?.slug) {
      this.state.setStatus(
        'This region has no web address yet: give it a slug in Regions first.',
        true
      );
      return;
    }
    const go = () => {
      const record = recordFor();
      if (!record.name || !record.slug) {
        this.state.setStatus("This page's name hasn't loaded yet: try again in a moment.", true);
        return;
      }
      const published = action === 'publish';
      const write = this.records.recordById(record._meta.id)
        ? this.records.freshWrite(record, (fresh) => ({ ...fresh, published }))
        : this.records.create({ ...record, published });
      write.subscribe({
        next: inZone(this.zone, (next: Suburb) => this.afterSave(next, action)),
        error: inZone(this.zone, (err: unknown) => this.records.showError(err)),
      });
    };
    if (action === 'publish') return go();
    const modal = this.modalService.generic({
      title: 'Take this page off the website?',
      copy: [
        'The page comes off the website preview now, and off picaluna.com when the preview is next published to production. Its content stays here, so you can publish it again.',
      ],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: 'p-button-text' },
        { label: 'Un-publish', key: 'unpublish', class: 'p-button-danger' },
      ],
    });
    modal?.onClose.pipe(take(1)).subscribe((key: string) => key === 'unpublish' && go());
  }

  /** A published suburb has a page: mark it covered, so the server keeps it in its region. */
  private markHasPage(code: string): void {
    const a = this.state.engine.areas.get(code);
    if (!a?.regionId || a.covered) return;
    this.api
      .edit([{ salCode: code, field: 'covered', from: '0', to: '1' }])
      .pipe(take(1))
      .subscribe({
        next: inZone(this.zone, () =>
          this.state.engine.applyRows([{ salCode: code, regionId: a.regionId, covered: true }])
        ),
        error: () => {}, // best-effort: the page is saved either way, and the next save or reload shows the truth
      });
  }

  /** Ship one record's page to the website preview: its suburbs.json row and its address, nothing else. */
  private ship(record: Suburb, action: PublishAction): void {
    const region = this.records.regions().find((r) => r._meta.id === record.regionId);
    if (!region) return;
    this.state.setStatus(
      `${action === 'publish' ? 'Publishing' : 'Un-publishing'} ${record.name}…`
    );
    this.site
      .publish(suburbPublishScope(region, record))
      .pipe(take(1))
      .subscribe({
        next: inZone(this.zone, () => this.shipped(record, action)),
        error: inZone(this.zone, (err: unknown) => this.notShipped(record, action, err)),
      });
  }

  private shipped(record: Suburb, action: PublishAction): void {
    const publishing = action === 'publish';
    this.state.setStatus(
      `${record.name}: ${publishing ? 'on its way to' : 'coming off'} the website preview.`
    );
    this.modalService.generic({
      title: publishing ? 'Publishing started' : 'Un-publishing started',
      copy: [
        publishing
          ? `The page for ${record.name} is being published to the website preview. It takes a few minutes.`
          : `The page for ${record.name} is coming off the website preview. It takes a few minutes.`,
        `Only this ${record.kind === SUBREGION_KIND ? 'sub-region' : 'suburb'} is published. Track it on the Website page, and publish to production from there when you are happy with the preview.`,
      ],
      buttons: [{ label: 'Close', key: 'close', class: 'p-button-text' }],
    });
    this.state.redraw();
  }

  /** Saved either way; flag the region so the next region publish ships it. */
  private notShipped(record: Suburb, action: PublishAction, err: unknown): void {
    this.records.markRegionChanged(record.regionId);
    const notSetUp = publishNotSetUp(err);
    this.state.setStatus(
      notSetUp
        ? `Saved ${record.name} as ${action === 'publish' ? 'published' : 'unpublished'}. Website publishing isn't set up on this Conductor, so it goes out with the next site publish.`
        : `Saved ${record.name}, but publishing didn't start. Publish its region from the Regions card.`,
      !notSetUp
    );
    this.state.redraw();
  }
}
